/**
 * <s-dispatch-countdown> — same-day preparation badge.
 *
 * Before the cutoff (data-cutoff, 0-23) on a working day (data-days, 0=Sun..6=Sat) in the shop
 * time zone (data-timezone), shows the [data-state="today"] message with a live countdown.
 * Otherwise shows [data-state="tomorrow"], or [data-state="monday"] when the next working day
 * is not tomorrow, so the badge is always truthful and always visible.
 */
class SDispatchCountdown extends HTMLElement {
  #timer;

  connectedCallback() {
    this.cutoff = Number(this.dataset.cutoff ?? 14);
    this.days = (this.dataset.days || '1,2,3,4,5').split(',').map(Number);
    this.formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.dataset.timezone || 'Europe/Madrid',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    this.states = [...this.querySelectorAll('[data-state]')];
    this.output = this.querySelector('[data-countdown-time]');
    if (sessionStorage.getItem('pureaura-dispatch-closed')) { this.hidden = true; return; }
    this.querySelector('[data-dismiss]')?.addEventListener('click', () => {
      this.hidden = true;
      clearInterval(this.#timer);
      sessionStorage.setItem('pureaura-dispatch-closed', '1');
    });
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #show(state) {
    for (const el of this.states) el.hidden = el.dataset.state !== state;
  }

  #tick() {
    const parts = Object.fromEntries(this.formatter.formatToParts(new Date()).map((p) => [p.type, p.value]));
    const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
    const elapsed = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    const remaining = this.cutoff * 3600 - elapsed;

    if (this.days.includes(weekday) && remaining > 0) {
      this.#show('today');
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.output) this.output.textContent = h > 0 ? `${h}h ${pad(m)}m ${pad(s)}s` : `${pad(m)}m ${pad(s)}s`;
      return;
    }

    // Outside hours: the next working day is either tomorrow or (weekends) Monday.
    const tomorrow = (weekday + 1) % 7;
    const nextIsTomorrow = this.days.includes(tomorrow);
    this.#show(nextIsTomorrow ? 'tomorrow' : 'monday');
  }
}

if (!customElements.get('s-dispatch-countdown')) customElements.define('s-dispatch-countdown', SDispatchCountdown);
